import React, { Component } from 'react';
import AvatarEditor from 'react-avatar-editor';
import { connect } from 'react-redux';

import { uploadVideoThumbnail } from 'creators/shareVideo';

import plusSrc from 'img/icons/new/add-white.svg';
import minusSrc from 'img/icons/new/minus-white.svg';
import DragToReposIllo from 'img/illustrations/drag-to-reposition.svg';

import ZoomSliderLevel from './zoom-slider-level';

import Slider from 'components/slider';
import Spinner from 'components/ui-components/circular-loading-spinner';

import './styles.less';

const MAX_ZOOM = 4;

const MIN_ZOOM = 1;

const RuleOfThirds = ({ height, width }) => {
  return (
    <div className="rule-of-thirds-container" style={{ width, height }}>
      <div
        className="rule-of-thirds-line horizontal"
        style={{ top: height / 3, width }}
      />
      <div
        className="rule-of-thirds-line horizontal"
        style={{ top: (2 * height) / 3, width }}
      />
      <div
        className="rule-of-thirds-line vertical"
        style={{ left: width / 3, height }}
      />
      <div
        className="rule-of-thirds-line vertical"
        style={{ left: (2 * width) / 3, height }}
      />
    </div>
  );
};

const ThumbnailCropper = class extends Component {
  state = {
    window_dims: { height: 0, width: 0 },
    scale: 1,
    position: { x: 0, y: 0 },
    dragging: false,
  };

  render() {
    const { thumbnailLocalUrl, currentlyUploading } = this.props;
    const { window_dims, scale, dragging, position } = this.state;

    return (
      <div
        ref={ref => (this.container = ref)}
        className="thumbnail-cropper-window"
      >
        {thumbnailLocalUrl && (
          <AvatarEditor
            position={position}
            ref={me => (this.cropper = me)}
            image={thumbnailLocalUrl}
            width={window_dims.width}
            height={window_dims.height}
            scale={scale}
            style={{ cursor: 'move' }}
            onMouseMove={this.handleMove}
            onMouseUp={this.handleInteractionEnd}
            onPositionChange={this.onPositionChange}
            border={0}
          />
        )}
        {!dragging && !currentlyUploading && thumbnailLocalUrl && (
          <img src={DragToReposIllo} className="thumb-drag-instructions" />
        )}
        {thumbnailLocalUrl && !currentlyUploading && (
          <RuleOfThirds height={window_dims.height} width={window_dims.width} />
        )}
        {currentlyUploading && <Spinner />}
        {thumbnailLocalUrl && !currentlyUploading && (
          <div className="thumb-zoom-pill">
            <img
              src={minusSrc}
              className="thumb-zoom-pill-scale"
              onClick={this.zoomOut}
            />
            <Slider
              ref={slider => (this.zoomSlider = slider)}
              label="Date Range"
              getPercent={this.getZoomPercent}
              valueNow={this.getZoomPercent}
              onMouseMove={this.onZoomSliderMove}
            >
              <ZoomSliderLevel />
            </Slider>
            <img
              src={plusSrc}
              className="thumb-zoom-pill-scale"
              onClick={this.zoomIn}
            />
          </div>
        )}
      </div>
    );
  }

  onPositionChange = position => {
    this.setState({ position });
  };

  handleMove = () => {
    const { dragging } = this.state;

    if (dragging) {
      return;
    }

    this.setState({ dragging: true });
  };

  handleInteractionEnd = () => {
    this.setState({ dragging: false });
  };

  onZoomSliderMove = e => {
    const newZoom = this.zoomSlider.calculateDistance(e);

    this.setState({ scale: MIN_ZOOM + newZoom * (MAX_ZOOM - MIN_ZOOM) });
  };

  getZoomPercent = () => {
    const { scale } = this.state;

    return (scale - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM);
  };

  zoomIn = () => {
    const { scale } = this.state;

    this.setState({ scale: Math.min(scale + 0.1, MAX_ZOOM) });
  };

  zoomOut = () => {
    const { scale } = this.state;

    this.setState({ scale: Math.max(scale - 0.1, MIN_ZOOM) });
  };

  handleThumbnailUpload = () => {
    if (this.props.currentlyUploading) {
      return;
    }

    const { videoId, thumbnailLocalUrl, ogImageDimensions } = this.props;
    const { scale, position } = this.state;
    const { width: ogWidth, height: ogHeight } = ogImageDimensions;
    const { x, y, width, height } = this.cropper.getCroppingRect();

    this.props.uploadVideoThumbnail(videoId, thumbnailLocalUrl, {
      x,
      y,
      width,
      height,
      full_width: ogWidth,
      full_height: ogHeight,
      position_info: { scale, position },
    });
  };

  updateDimension = () => {
    const { height, width } = this.container.getBoundingClientRect();

    this.setState({ window_dims: { height, width } });
  };

  setOgThumbnailPositioning = () => {
    const {
      position_info: { position, scale },
    } = this.props.ogThumbnailDims;

    this.setState({ position, scale });
  };

  componentDidMount() {
    this.updateDimension();

    window.addEventListener('resize', this.updateDimension);

    if (this.props.ogThumbnailDims) {
      this.setOgThumbnailPositioning();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.shouldSave && this.props.shouldSave) {
      this.handleThumbnailUpload();
    }

    if (!prevProps.ogThumbnailDims && this.props.ogThumbnailDims) {
      this.setOgThumbnailPositioning();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimension);
  }
};

const mapStateToProps = state => {
  const { thumbnail_data } = state.editor;
  const { video } = state.share_video;

  const thumbnails = (video && video.thumbnails) || {};

  return {
    thumbnailLocalUrl: thumbnail_data.thumbnail_local,
    ogImageDimensions: thumbnail_data.og_dims,
    shouldSave: thumbnail_data.initiate_save,
    currentlyUploading: thumbnail_data.uploading,
    ogThumbnailDims: (thumbnails || {}).og_thumbnail_crop_dims,
  };
};

const mapDispatchToProps = dispatch => ({
  uploadVideoThumbnail: (videoId, files, dims) =>
    dispatch(uploadVideoThumbnail(videoId, files, dims)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ThumbnailCropper);
